import { Box, Divider, Flex, Switch, Text } from '@televet/kibble-ui';
import { useTracking } from 'shared/providers/TrackingProvider';
import { disable as disableDarkMode, enable as enableDarkMode } from 'darkreader';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FeatureFlagName } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import usePersistedState from 'shared/hooks/usePersistedState';
import { useAuth } from 'shared/providers/AuthProvider';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import OttoLabsClinicSettingsList from './components/OttoLabsClinicSettingsList';
import { SettingsOttoLabsEvents } from 'shared/providers/TrackingProvider/EventNames';
const darkSettings = {
  brightness: 100,
  contrast: 90,
  sepia: 10,
};

export const DarkModeOverwriteCSS = `
*::-webkit-scrollbar-track
{
  -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.3) !important;
  background-color: rgba(255, 255, 255, 0.08) !important;
}

*::-webkit-scrollbar
{
  width: 6px !important;
  height: 6px !important;
}

*::-webkit-scrollbar-thumb
{
  background-color: rgba(255, 255, 255, 0.16) !important;
  border-radius: 5px !important;
}
`;

export const turnOnDarkMode = (): void => {
  enableDarkMode(darkSettings, {
    invert: [],
    css: DarkModeOverwriteCSS,
    ignoreInlineStyle: [],
    ignoreImageAnalysis: ['svg', 'img'],
  });
};

export const turnOffDarkMode = (): void => {
  disableDarkMode();
};

const OttoLabsSettings = (): JSX.Element => {
  const { isAppointmentCreateWritebackSupported } = useIntegrationsProvider();
  const { hasAdminRoleAtCurrentClinic } = useClinicUser();
  const { isImpersonating } = useAuth();
  const [isReaderModeEnabled, setIsReaderModeEnabled] = usePersistedState('isReaderModeEnabled', false);
  const [isDictationEnabled, setIsDictationEnabled] = usePersistedState('isDictationEnabled', false);
  const { trackEvent } = useTracking();

  // Deprecating this in favor of Kibble's toggleColorMode
  const toggleDarkMode = useCallback(() => {
    setIsReaderModeEnabled((isReaderModeEnabled) => {
      trackEvent(SettingsOttoLabsEvents.ToggleDarkMode, { isEnabled: isReaderModeEnabled });
      return !isReaderModeEnabled;
    });
  }, [setIsReaderModeEnabled, trackEvent]);

  const toggleDictation = useCallback(() => {
    setIsDictationEnabled((isDictationEnabled) => {
      trackEvent(SettingsOttoLabsEvents.ToggleDictation, { isEnabled: isDictationEnabled });
      return !isDictationEnabled;
    });
  }, [setIsDictationEnabled, trackEvent]);

  useEffect(() => {
    if (isReaderModeEnabled) {
      turnOnDarkMode();
    } else disableDarkMode();
  }, [isReaderModeEnabled]);

  const clinicSettingsFlags = useMemo(() => {
    const base = [FeatureFlagName.SortConversationByLastMessage];
    if (isAppointmentCreateWritebackSupported) {
      base.push(FeatureFlagName.FlowAppointmentCreationPimsWriteback);
    }
    return base;
  }, [isAppointmentCreateWritebackSupported]);

  return (
    <Box className="OttoLabs__Container">
      <Box mb={3}>
        Otto Labs allows users to activate and test new and experimental features. Some features will apply to all
        members of a clinic while others apply only to your account. These features may not be as stable as the rest of
        the application, and are being made available to test the applicability and effectiveness within Otto Flow.
        Feedback is greatly appreciated.
      </Box>
      <Divider />
      <Flex margin="10px 0" alignItems="center">
        <Text size="xl">User Settings</Text>
      </Flex>
      <Text mb="24px">These will only affect your personal experience.</Text>
      {/* Dark Mode */}
      <Flex>
        <Flex width={'300px'} alignItems="center">
          <Switch isChecked={isReaderModeEnabled} onChange={toggleDarkMode} label="Dark Mode" isFieldInline />
        </Flex>
        <Box flex="1">
          <Text>
            Change the color scheme of Otto Flow to a dark background with light text and graphics. Many people prefer
            Dark Mode when working in lower light conditions.
          </Text>
        </Box>
      </Flex>
      {/* Dictation */}
      <Flex marginTop={4}>
        <Flex width={'300px'} alignItems="center">
          <Switch isChecked={isDictationEnabled} onChange={toggleDictation} label="Dictation" isFieldInline />
        </Flex>
        <Box flex="1">
          <Text>
            Enable speech-to-text functionality to effortlessly dictate messages or notes. To utilize speech-to-text,
            click the microphone icon within the Message and Note typing area. Simply start speaking, and advanced
            algorithms will convert your speech into written text in real-time. Don&apos;t forget to review and edit the
            message before sending or saving.
          </Text>
        </Box>
      </Flex>
      <Divider padding="16px 0" />
      {(hasAdminRoleAtCurrentClinic || isImpersonating) && <OttoLabsClinicSettingsList flags={clinicSettingsFlags} />}
    </Box>
  );
};

export default OttoLabsSettings;
